import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export function FieldInfo(props: { label: string; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${props.label}`}
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          <Info className="size-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 text-xs leading-relaxed">
        {props.children}
      </PopoverContent>
    </Popover>
  );
}

function FieldLabel(props: { htmlFor: string; label: string; help?: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={props.htmlFor}>{props.label}</Label>
      {props.help ? <FieldInfo label={props.label}>{props.help}</FieldInfo> : null}
    </div>
  );
}

export function TextField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  help?: ReactNode;
  mono?: boolean;
  type?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={props.id} label={props.label} help={props.help} />
      <Input
        id={props.id}
        type={props.type ?? "text"}
        value={props.value}
        placeholder={props.placeholder}
        autoComplete={props.autoComplete}
        required={props.required}
        data-numeric={props.mono ? true : undefined}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </div>
  );
}

export function TimezoneField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  help?: ReactNode;
}) {
  const listId = `${props.id}-options`;
  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={props.id} label={props.label} help={props.help} />
      <Input
        id={props.id}
        value={props.value}
        list={listId}
        required={props.required}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
      />
      <datalist id={listId}>
        {Intl.supportedValuesOf("timeZone").map((tz) => (
          <option key={tz} value={tz}>
            {tz}
          </option>
        ))}
      </datalist>
    </div>
  );
}

export function NumberField(props: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  help?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={props.id} label={props.label} help={props.help} />
      <Input
        id={props.id}
        type="number"
        value={props.value}
        step={props.step ?? 1}
        disabled={props.disabled}
        data-numeric
        onChange={(e) => props.onChange(Number(e.target.value))}
      />
    </div>
  );
}

export function SelectField<T extends string>(props: {
  id: string;
  label: string;
  value: T;
  options: readonly T[];
  onChange: (value: T) => void;
  help?: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={props.id} label={props.label} help={props.help} />
      <Select value={props.value} onValueChange={(value) => props.onChange(value as T)}>
        <SelectTrigger id={props.id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {props.options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function SwitchRow(props: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  help?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-6">
      <FieldLabel htmlFor={props.id} label={props.label} help={props.help} />
      <Switch id={props.id} checked={props.checked} onCheckedChange={props.onChange} />
    </div>
  );
}
